import { cn } from "@-/com.npmjs.cn";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@-/dev.improve.haniverse.components.ui.tooltip";
import {
  formatHanAmount,
  formatHanMilesAmount,
  HAN_COIN_ICON_CLASS_NAME,
  HAN_CURRENCY_MARK,
  HAN_MILES_MAX_FARE_SHARE,
  HAN_MILES_PER_HAN,
  hanForRating,
  useHanWalletQueryOptions,
} from "@-/dev.improve.haniverse.lib.han-currency";
import { REVIEW_RATING } from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";
import { View } from "@-/com.npmjs.react-native";
import { useIsNavAccountDrawerOpen } from "#nav-account-drawer-context";

/**
 * Wallet stats in the profile surface's body: Han on the left, HanMiles on the right.
 * Tapping the stats explains how Han and HanMiles are earned.
 *
 * The wallet sums every review, so it is only read while the drawer is open; a closed drawer keeps
 * the last balance on screen instead of re-reading it after each rating.
 */
export function NavAccountDrawerWallet() {
  const isOpen = useIsNavAccountDrawerOpen();
  const { data: wallet } = useQuery({
    ...useHanWalletQueryOptions(),
    enabled: isOpen,
    placeholderData: keepPreviousData,
    throwOnError: false,
  });
  // Reserve both balances before the first read so the profile surface never shifts.
  const isLoaded = wallet !== undefined;
  const { dragons, han, hanMiles } = wallet ?? { dragons: 0, han: 0, hanMiles: 0 };
  const accessibilityLabel = [
    `${han} Han`,
    ...(dragons > 0 ? [`${dragons} dragons`] : []),
    `${hanMiles} HanMiles`,
  ].join(", ");
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger
        accessibilityElementsHidden={!isLoaded}
        accessibilityHint="Explains how Han and HanMiles are earned"
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button"
        className={cn(
          "w-full flex-row items-center justify-between gap-3 rounded-xs active:bg-accent",
          !isLoaded && "opacity-0",
        )}
        disabled={!isLoaded}
        hitSlop={8}
        testID="nav-profile-drawer-wallet"
      >
        <View className="min-w-0 flex-1 flex-row items-center gap-1.5">
          <Icon className={HAN_COIN_ICON_CLASS_NAME} name="HanCashCoin" size={18} />
          <Text
            className="shrink font-sans text-base font-semibold tabular-nums"
            numberOfLines={1}
            testID="nav-profile-drawer-wallet-han"
          >
            {formatHanAmount(han)}
          </Text>
          {dragons > 0 ? (
            <View className="flex-row items-center gap-0.5">
              <Icon className="text-foreground" name="HanDragon" size={18} />
              <Text className="font-sans text-base font-semibold tabular-nums">{dragons}</Text>
            </View>
          ) : null}
        </View>
        <View
          className="min-w-0 flex-1 flex-row items-center justify-end gap-1.5"
          testID="nav-profile-drawer-wallet-han-miles"
        >
          <Icon className="text-foreground" name="MdFlightTakeoff" size={16} />
          <Text className="shrink font-sans text-base font-semibold tabular-nums" numberOfLines={1}>
            {formatHanMilesAmount(hanMiles)}
          </Text>
        </View>
      </TooltipTrigger>
      <TooltipContent className="max-w-72" side="bottom">
        <Text className="font-semibold">Han {HAN_CURRENCY_MARK}</Text>
        <Text>
          Every review earns {hanForRating(REVIEW_RATING.Good)} (1 for Again), and the Welcome Back
          card holds a 红包 each day: Han, HanMiles, a day of bonus Han on every review, and a
          dragon 龙 every 7th day.
        </Text>
        <Text className="mt-1 font-semibold">HanMiles ✈</Text>
        <Text>
          Every km you travel on the map earns a HanMile. {HAN_MILES_PER_HAN} HanMiles take{" "}
          {HAN_CURRENCY_MARK} 1 off a flight, up to {Math.round(HAN_MILES_MAX_FARE_SHARE * 100)}% of
          its fare.
        </Text>
      </TooltipContent>
    </Tooltip>
  );
}
